import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {Button, Icon, Label, Loader, Message, Segment} from 'semantic-ui-react';
import {listMine, deleteSample} from './api';

const Page = styled.div`
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;

const SampleRow = styled(Segment)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Stat = styled.span`
  margin-right: 12px;
  font-size: 0.9em;
  color: var(--br-muted);
`;

const SampleChaptersHome = () => {
  const idToken = useSelector((s) => s.user.idToken);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const data = await listMine(idToken);
      setItems(data);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (idToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this sample chapter and all its ratings?')) return;
    try {
      await deleteSample(idToken, id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Page>
      <Header>
        <h1>Sample chapters (Chapter 1 tester)</h1>
        <div>
          <Button as={Link} to="/sample-chapters/discover" basic>
            <Icon name="search" /> Browse
          </Button>
          <Button primary as={Link} to="/sample-chapters/new">
            <Icon name="plus" /> New sample
          </Button>
        </div>
      </Header>

      <p>
        Publish only the first chapter of your book — text, audio, or both —
        and let readers tell you whether they would keep reading. Create
        multiple variants of the same chapter to A/B test alternatives.
      </p>

      {error && <Message error content={error} />}
      {!items && <Loader active inline="centered" />}
      {items && items.length === 0 && (
        <Segment placeholder>
          <p>You haven't created any sample chapters yet.</p>
          <Button primary as={Link} to="/sample-chapters/new">
            Create your first sample
          </Button>
        </Segment>
      )}
      {items &&
        items.map((s) => (
          <SampleRow key={s._id}>
            <div>
              <strong>{s.title}</strong>{' '}
              <Label size="tiny">Variant {s.variantLabel}</Label>{' '}
              {s.listed ? (
                <Label color="green" size="tiny">
                  Listed
                </Label>
              ) : (
                <Label size="tiny">Draft</Label>
              )}
              {s.audioUrl && (
                <Label color="violet" size="tiny">
                  <Icon name="volume up" /> Audio
                </Label>
              )}
              {!s.audioUrl && (
                <Label size="tiny">
                  <Icon name="font" /> Text only
                </Label>
              )}
              <div>
                <Stat>
                  <Icon name="users" />
                  {s.ratingsCount || 0} ratings
                </Stat>
                <Stat>
                  <Icon name="thumbs up" color="green" />
                  {s.yesCount || 0}
                </Stat>
                <Stat>
                  <Icon name="thumbs down" color="grey" />
                  {s.noCount || 0}
                </Stat>
                <Stat>
                  <Icon name="mail" />
                  {s.contactOptInCount || 0} opted in for contact
                </Stat>
              </div>
            </div>
            <div>
              <Button as={Link} to={`/sample-chapters/read/${s._id}`} basic>
                <Icon name="eye" /> Preview
              </Button>
              <Button as={Link} to={`/sample-chapters/edit/${s._id}`}>
                <Icon name="edit" /> Edit
              </Button>
              <Button basic color="red" onClick={() => handleDelete(s._id)}>
                <Icon name="trash" /> Delete
              </Button>
            </div>
          </SampleRow>
        ))}
    </Page>
  );
};

export default SampleChaptersHome;
